import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Briefcase, Clock, FileText, Users, GraduationCap, Globe, ArrowLeft,
  Search, ChevronRight, MapPin, DollarSign, Building, Loader2, Plus,
  ExternalLink, Mail, CheckCircle, Sparkles, Award, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIRECTORY_LOGO = "https://customer-assets.emergentagent.com/job_supportcenter-3/artifacts/t4g4tqes_New%20Branding%20CharactersDesign%20%281200%20x%201200%20px%29%20%28300%20x%20300%20px%29%20%281%20x%201%20in%29%20%281%20x%201%20in%29%20%28350%20x%20350%20px%29%20%2816%29.png";

const JOB_CATEGORY_ICONS = {
  'full-time': Briefcase,
  'part-time': Clock,
  'contract': FileText,
  'freelance': Users,
  'internship': GraduationCap,
  'remote': Globe,
};

const JOB_CATEGORY_COLORS = {
  'full-time': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  'part-time': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  'contract': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  'freelance': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  'internship': { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
  'remote': { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
};

export default function DirectoryJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [showPostForm, setShowPostForm] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [posting, setPosting] = useState(false);
  const [newJob, setNewJob] = useState({
    title: '', company_name: '', category: 'full-time', description: '',
    location: '', salary_min: '', salary_max: '', salary_type: 'yearly',
    remote: false, requirements: '', benefits: '', contact_email: '', apply_url: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedCategory, remoteOnly]);

  const loadData = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/directory/jobs?limit=100`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      if (remoteOnly) url += `&remote=true`;
      
      const [jobsRes, catsRes] = await Promise.all([
        fetch(url),
        fetch(`${API_URL}/api/directory/jobs/categories`)
      ]);
      
      setJobs(await jobsRes.json());
      setCategories(await catsRes.json());
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async (e) => {
    e.preventDefault();
    if (!newJob.title || !newJob.company_name || !newJob.description || !newJob.contact_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setPosting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/directory/jobs`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newJob,
          salary_min: newJob.salary_min ? parseFloat(newJob.salary_min) : null,
          salary_max: newJob.salary_max ? parseFloat(newJob.salary_max) : null,
          requirements: newJob.requirements ? newJob.requirements.split('\n').filter(r => r.trim()) : [],
          benefits: newJob.benefits ? newJob.benefits.split('\n').filter(b => b.trim()) : [],
        })
      });
      
      if (res.ok) {
        toast.success('Job posted successfully!');
        setShowPostForm(false);
        setNewJob({
          title: '', company_name: '', category: 'full-time', description: '',
          location: '', salary_min: '', salary_max: '', salary_type: 'yearly',
          remote: false, requirements: '', benefits: '', contact_email: '', apply_url: ''
        });
        loadData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to post job');
      }
    } catch (error) {
      toast.error('Failed to post job');
    } finally {
      setPosting(false);
    }
  };

  const filteredJobs = jobs.filter(job =>
    job.title.toLowerCase().includes(search.toLowerCase()) ||
    job.company_name.toLowerCase().includes(search.toLowerCase()) ||
    job.description.toLowerCase().includes(search.toLowerCase())
  );

  const getCategoryInfo = (slug) => categories.find(c => c.slug === slug) || { name: slug };
  const getCategoryColors = (slug) => JOB_CATEGORY_COLORS[slug] || JOB_CATEGORY_COLORS['full-time'];

  const formatSalary = (job) => {
    if (!job.salary_min && !job.salary_max) return null;
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    if (job.salary_min && job.salary_max) {
      return `${formatter.format(job.salary_min)} - ${formatter.format(job.salary_max)}`;
    }
    return formatter.format(job.salary_min || job.salary_max);
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-[#0d1320]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/directory" className="flex items-center gap-3">
              <img src={DIRECTORY_LOGO} alt="AI Horse-Powered" className="w-10 h-10 rounded-lg" />
              <div className="hidden sm:block">
                <span className="font-semibold text-white text-[15px]">AI Horse-Powered</span>
                <p className="text-[11px] text-gray-500">Job Board</p>
              </div>
            </Link>
            
            <div className="flex items-center gap-6">
              <div className="hidden md:flex items-center gap-5">
                <Link to="/directory" className="text-gray-400 hover:text-white text-sm transition-colors">Directory</Link>
                <Link to="/directory/deals" className="text-gray-400 hover:text-white text-sm transition-colors">Deals</Link>
                <Link to="/directory/classifieds" className="text-gray-400 hover:text-white text-sm transition-colors">Classifieds</Link>
              </div>
              <Button 
                size="sm" 
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                onClick={() => setShowPostForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" /> Post Job
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-14 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-950/20 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-cyan-500/5 blur-[100px] rounded-full" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
            Find your dream job
          </h1>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Discover opportunities from AI Certified businesses in the AI Horse-Powered network.
          </p>

          {/* Search */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search jobs, companies, or keywords..."
                className="w-full pl-14 pr-6 py-4 text-[16px] bg-white rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 shadow-xl shadow-black/20"
                data-testid="job-search-input"
              />
            </div>
          </div>

          {/* Quick Stats */}
          <div className="flex items-center justify-center gap-8">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{jobs.length}</p>
              <p className="text-xs text-gray-500">Open Jobs</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <p className="text-2xl font-bold text-cyan-400">{jobs.filter(j => j.remote).length}</p>
              <p className="text-xs text-gray-500">Remote</p>
            </div>
            <div className="w-px h-8 bg-white/10" />
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-400">{jobs.filter(j => j.is_featured).length}</p>
              <p className="text-xs text-gray-500">Featured</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 lg:px-6 pb-20">
        {/* Back Link */}
        <Link to="/directory" className="inline-flex items-center gap-2 text-gray-500 hover:text-white text-sm mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Directory
        </Link>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          <Button
            variant={selectedCategory === '' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory('')}
            className={selectedCategory === '' 
              ? 'bg-cyan-600 hover:bg-cyan-700 text-white border-0' 
              : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white'}
          >
            All Jobs
          </Button>
          {categories.map((cat) => {
            const Icon = JOB_CATEGORY_ICONS[cat.slug] || Briefcase;
            const isSelected = selectedCategory === cat.slug;
            return (
              <Button
                key={cat.slug}
                variant={isSelected ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.slug)}
                className={isSelected 
                  ? 'bg-cyan-600 hover:bg-cyan-700 text-white border-0' 
                  : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white'}
              >
                <Icon className="w-3.5 h-3.5 mr-1.5" /> {cat.name}
              </Button>
            );
          })}
          <Button
            variant={remoteOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setRemoteOnly(!remoteOnly)}
            className={remoteOnly 
              ? 'bg-cyan-600 hover:bg-cyan-700 text-white border-0' 
              : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:text-white'}
          >
            <Globe className="w-3.5 h-3.5 mr-1.5" /> Remote Only
          </Button>
        </div>

        {/* Jobs List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
            <Briefcase className="w-14 h-14 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Jobs Found</h3>
            <p className="text-gray-500 mb-4">Be the first to post a job opening!</p>
            <Button onClick={() => setShowPostForm(true)} className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Post a Job
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredJobs.map((job) => {
              const catColors = getCategoryColors(job.category);
              const Icon = JOB_CATEGORY_ICONS[job.category] || Briefcase;
              const salary = formatSalary(job);
              
              return (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileHover={{ y: -2 }}
                  onClick={() => setSelectedJob(job)}
                  className="bg-white rounded-xl p-5 border border-gray-100 hover:border-cyan-200 hover:shadow-lg transition-all cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold text-gray-900 text-[16px] mb-1">{job.title}</h3>
                          <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <Building className="w-4 h-4" />
                            <span>{job.company_name}</span>
                            <span className="text-gray-300">•</span>
                            <MapPin className="w-4 h-4" />
                            <span>{job.location}</span>
                            {job.remote && (
                              <>
                                <span className="text-gray-300">•</span>
                                <span className="flex items-center gap-1 text-cyan-600">
                                  <Globe className="w-3 h-3" /> Remote
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className={`px-2.5 py-1 rounded-lg ${catColors.bg} ${catColors.border} border`}>
                          <span className={`text-xs font-medium ${catColors.text}`}>{getCategoryInfo(job.category).name}</span>
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm line-clamp-2 mt-2">{job.description}</p>
                      <div className="flex items-center gap-4 mt-3">
                        {salary && (
                          <div className="flex items-center gap-1 text-emerald-600 font-medium text-sm">
                            <DollarSign className="w-4 h-4" />
                            <span>{salary}/{job.salary_type}</span>
                          </div>
                        )}
                        {job.is_featured && (
                          <span className="flex items-center gap-1 text-amber-600 text-sm">
                            <Award className="w-4 h-4" /> Featured
                          </span>
                        )}
                        <span className="text-gray-400 text-xs ml-auto">
                          {new Date(job.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Job Detail Modal */}
      <AnimatePresence>
        {selectedJob && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedJob(null)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center">
                    {(() => {
                      const Icon = JOB_CATEGORY_ICONS[selectedJob.category] || Briefcase;
                      return <Icon className="w-7 h-7 text-white" />;
                    })()}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{selectedJob.title}</h2>
                    <p className="text-gray-500">{selectedJob.company_name}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedJob(null)} className="text-gray-400 hover:text-gray-600 p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                <span className={`px-3 py-1 rounded-lg ${getCategoryColors(selectedJob.category).bg} ${getCategoryColors(selectedJob.category).text} text-sm font-medium`}>
                  {getCategoryInfo(selectedJob.category).name}
                </span>
                {selectedJob.remote && (
                  <span className="px-3 py-1 rounded-lg bg-cyan-50 text-cyan-700 text-sm font-medium">
                    <Globe className="w-3 h-3 inline mr-1" /> Remote
                  </span>
                )}
                <span className="px-3 py-1 rounded-lg bg-gray-100 text-gray-700 text-sm">
                  <MapPin className="w-3 h-3 inline mr-1" /> {selectedJob.location}
                </span>
              </div>

              {formatSalary(selectedJob) && (
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100 mb-5">
                  <p className="text-sm text-emerald-600 mb-1">Salary Range</p>
                  <p className="text-xl font-bold text-emerald-700">
                    {formatSalary(selectedJob)}<span className="text-sm font-normal">/{selectedJob.salary_type}</span>
                  </p>
                </div>
              )}

              <div className="mb-5">
                <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600 whitespace-pre-line text-[15px] leading-relaxed">{selectedJob.description}</p>
              </div>

              {selectedJob.requirements?.length > 0 && (
                <div className="mb-5">
                  <h3 className="font-semibold text-gray-900 mb-2">Requirements</h3>
                  <ul className="space-y-2">
                    {selectedJob.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-600 text-[15px]">
                        <CheckCircle className="w-5 h-5 text-cyan-500 mt-0.5 flex-shrink-0" />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedJob.benefits?.length > 0 && (
                <div className="mb-5">
                  <h3 className="font-semibold text-gray-900 mb-2">Benefits</h3>
                  <ul className="space-y-2">
                    {selectedJob.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-600 text-[15px]">
                        <Sparkles className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-gray-100">
                {selectedJob.apply_url ? (
                  <a href={selectedJob.apply_url} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-3">
                      <ExternalLink className="w-4 h-4 mr-2" /> Apply Now
                    </Button>
                  </a>
                ) : (
                  <a href={`mailto:${selectedJob.contact_email}?subject=Application for ${selectedJob.title}`} className="flex-1">
                    <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-3">
                      <Mail className="w-4 h-4 mr-2" /> Apply via Email
                    </Button>
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Post Job Modal */}
      <AnimatePresence>
        {showPostForm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPostForm(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-gray-900">Post a Job</h2>
                <button onClick={() => setShowPostForm(false)} className="text-gray-400 hover:text-gray-600 p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handlePost} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">Job Title *</label>
                    <Input
                      value={newJob.title}
                      onChange={(e) => setNewJob({...newJob, title: e.target.value})}
                      placeholder="e.g., Senior Developer"
                      className="bg-gray-50 border-gray-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">Company Name *</label>
                    <Input
                      value={newJob.company_name}
                      onChange={(e) => setNewJob({...newJob, company_name: e.target.value})}
                      placeholder="Your company name"
                      className="bg-gray-50 border-gray-200"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">Job Type *</label>
                    <select
                      value={newJob.category}
                      onChange={(e) => setNewJob({...newJob, category: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm"
                    >
                      <option value="full-time">Full-Time</option>
                      <option value="part-time">Part-Time</option>
                      <option value="contract">Contract</option>
                      <option value="freelance">Freelance</option>
                      <option value="internship">Internship</option>
                      <option value="remote">Remote</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">Location *</label>
                    <Input
                      value={newJob.location}
                      onChange={(e) => setNewJob({...newJob, location: e.target.value})}
                      placeholder="City, State or Remote"
                      className="bg-gray-50 border-gray-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-600 mb-1.5 block">Description *</label>
                  <Textarea
                    value={newJob.description}
                    onChange={(e) => setNewJob({...newJob, description: e.target.value})}
                    placeholder="Describe the role..."
                    rows={4}
                    className="bg-gray-50 border-gray-200"
                  />
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">Min Salary</label>
                    <Input
                      type="number"
                      value={newJob.salary_min}
                      onChange={(e) => setNewJob({...newJob, salary_min: e.target.value})}
                      placeholder="50000"
                      className="bg-gray-50 border-gray-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">Max Salary</label>
                    <Input
                      type="number"
                      value={newJob.salary_max}
                      onChange={(e) => setNewJob({...newJob, salary_max: e.target.value})}
                      placeholder="80000"
                      className="bg-gray-50 border-gray-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">Salary Type</label>
                    <select
                      value={newJob.salary_type}
                      onChange={(e) => setNewJob({...newJob, salary_type: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm"
                    >
                      <option value="yearly">Yearly</option>
                      <option value="hourly">Hourly</option>
                      <option value="project">Per Project</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="remote"
                    checked={newJob.remote}
                    onChange={(e) => setNewJob({...newJob, remote: e.target.checked})}
                    className="w-4 h-4 text-cyan-600 rounded"
                  />
                  <label htmlFor="remote" className="text-sm text-gray-700">This is a remote position</label>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">Contact Email *</label>
                    <Input
                      type="email"
                      value={newJob.contact_email}
                      onChange={(e) => setNewJob({...newJob, contact_email: e.target.value})}
                      placeholder="hr@company.com"
                      className="bg-gray-50 border-gray-200"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600 mb-1.5 block">Apply URL</label>
                    <Input
                      value={newJob.apply_url}
                      onChange={(e) => setNewJob({...newJob, apply_url: e.target.value})}
                      placeholder="https://..."
                      className="bg-gray-50 border-gray-200"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white" disabled={posting}>
                    {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Post Job'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowPostForm(false)} className="border-gray-300 text-gray-700">
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="py-10 px-4 bg-[#070b12] border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <img src={DIRECTORY_LOGO} alt="AI Horse-Powered" className="w-8 h-8 rounded-lg" />
            <span className="font-semibold text-white text-sm">AI Horse-Powered Job Board</span>
          </div>
          <p className="text-sm text-gray-600">© {new Date().getFullYear()} AI Horse-Powered Technology</p>
        </div>
      </footer>
    </div>
  );
}
